import * as React from 'react';

import { cn } from '@/lib/utils';

const fieldBase =
  'w-full rounded-md border border-line bg-surface text-ink placeholder:text-ink-4 ' +
  'transition-colors focus-visible:border-primary focus-visible:outline-none ' +
  'focus-visible:ring-1 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-60';

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, type = 'text', ...props }, ref) {
    return (
      <input
        ref={ref}
        type={type}
        className={cn(fieldBase, 'h-8 px-2.5 text-[13px]', className)}
        {...props}
      />
    );
  },
);

export const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, rows = 3, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(fieldBase, 'resize-y px-2.5 py-2 text-[13px] leading-relaxed', className)}
      {...props}
    />
  );
});

/** Native select, kept for dense filter bars where a Radix popover is overkill. */
export const NativeSelect = React.forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement>
>(function NativeSelect({ className, ...props }, ref) {
  return (
    <select
      ref={ref}
      className={cn(
        fieldBase,
        'h-8 cursor-pointer appearance-none bg-[length:14px] bg-[right_0.5rem_center] bg-no-repeat pl-2.5 pr-7 text-[13px]',
        className,
      )}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23888' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
      }}
      {...props}
    />
  );
});

export function Label({
  className,
  children,
  ...props
}: React.LabelHTMLAttributes<HTMLLabelElement>) {
  return (
    <label
      className={cn(
        'text-[11px] font-medium uppercase tracking-wide text-ink-3',
        className,
      )}
      {...props}
    >
      {children}
    </label>
  );
}

/** Label + control + error, so forms stay consistent without repeating markup. */
export function Field({
  label,
  htmlFor,
  hint,
  error,
  required,
  className,
  children,
}: {
  label?: React.ReactNode;
  htmlFor?: string;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {label ? (
        <Label htmlFor={htmlFor}>
          {label}
          {required ? <span className="ml-0.5 text-danger">*</span> : null}
        </Label>
      ) : null}
      {children}
      {error ? (
        <p className="text-[11px] text-danger">{error}</p>
      ) : hint ? (
        <p className="text-[11px] text-ink-4">{hint}</p>
      ) : null}
    </div>
  );
}

/** Magnifier-prefixed search box used by every list page. */
export const SearchInput = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & { icon?: React.ReactNode }
>(function SearchInput({ className, icon, ...props }, ref) {
  return (
    <div className="relative flex-1">
      {icon ? (
        <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-4">
          {icon}
        </span>
      ) : null}
      <input
        ref={ref}
        type="search"
        className={cn(fieldBase, 'h-8 pr-2.5 text-[13px]', icon ? 'pl-8' : 'pl-2.5', className)}
        {...props}
      />
    </div>
  );
});

export { fieldBase };
